// Notificações no celular via ntfy (pedido 2026-07-10). O usuário instala o
// app ntfy (iOS/Android), assina um TÓPICO, e o Apolo publica nele quando:
//   • um AGENTE termina um run (agendado ou Play; cloud e local)
//   • um TURNO de sessão termina (Chat e Code) — só turnos longos (anti-spam)
// Config = user_settings (NTFY_TOPIC/SERVER/TOKEN + flags por categoria), via
// PUT /api/settings/:key que já existe. Autocontido: lê GET /api/settings.
import { useCallback, useEffect, useState } from 'react';
import { Bell, Check, Loader2, RefreshCw } from 'lucide-react';
import { apiFetch } from '../../lib/api.js';

function randomTopic() {
  const bytes = new Uint8Array(9);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `apolo-${hex}`;
}

async function putSetting(key, value) {
  return apiFetch(`/api/settings/${key}`, { method: 'PUT', body: { value } });
}

/** Lê user_settings e devolve o estado ntfy (defaults aplicados). */
async function fetchNtfyState() {
  const res = await apiFetch('/api/settings');
  if (!res.ok) return null;
  const map = {};
  for (const s of res.data?.settings || []) map[s.key] = s;
  return {
    topic: map.NTFY_TOPIC?.value || '',
    server: map.NTFY_SERVER?.value || '',
    hasToken: !!map.NTFY_TOKEN?.has_value,
    notifyAgents: map.NTFY_NOTIFY_AGENTS?.value !== '0',
    notifySessions: map.NTFY_NOTIFY_SESSIONS?.value !== '0',
  };
}

/** Salva tópico/servidor/token + flags. Retorna a 1ª resposta com erro (ou null). */
async function saveNtfyState({ topic, server, token, notifyAgents, notifySessions }) {
  const results = [await putSetting('NTFY_TOPIC', topic.trim())];
  if (server.trim()) results.push(await putSetting('NTFY_SERVER', server.trim()));
  if (token.trim()) results.push(await putSetting('NTFY_TOKEN', token.trim()));
  results.push(await putSetting('NTFY_NOTIFY_AGENTS', notifyAgents ? '1' : '0'));
  results.push(await putSetting('NTFY_NOTIFY_SESSIONS', notifySessions ? '1' : '0'));
  return results.find((r) => !r.ok) || null;
}

export default function NtfySection() {
  const [topic, setTopic] = useState('');
  const [token, setToken] = useState('');
  const [server, setServer] = useState('');
  const [notifyAgents, setNotifyAgents] = useState(true);
  const [notifySessions, setNotifySessions] = useState(true);
  const [hasToken, setHasToken] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let stale = false;
    fetchNtfyState().then((s) => {
      if (stale) return;
      setLoading(false);
      if (!s) return;
      setTopic(s.topic); setServer(s.server); setHasToken(s.hasToken);
      setNotifyAgents(s.notifyAgents); setNotifySessions(s.notifySessions);
    });
    return () => { stale = true; };
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const bad = await saveNtfyState({ topic, server, token, notifyAgents, notifySessions });
      if (bad) { setError(bad.error || 'falha ao salvar'); return; }
      if (token.trim()) { setHasToken(true); setToken(''); }
      setSaved(true);
    } finally {
      setSaving(false);
    }
  }, [topic, server, token, notifyAgents, notifySessions]);

  const fields = { topic, setTopic, server, setServer, token, setToken, hasToken };
  const toggles = { notifyAgents, setNotifyAgents, notifySessions, setNotifySessions };

  return (
    <section>
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-apolo-textTertiary">
        <Bell size={11} aria-hidden="true" />
        Celular (ntfy)
      </div>
      <div className="rounded-lg border border-apolo-borderSubtle bg-apolo-surface2 p-3">
        {loading ? (
          <div className="flex items-center gap-2 py-2 text-xs text-apolo-textTertiary">
            <Loader2 size={12} className="animate-spin" aria-hidden="true" /> Carregando…
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            <NtfyFieldset {...fields} />
            <NtfyToggles {...toggles} />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="rounded-md bg-apolo-accent px-3 py-1.5 text-xs font-medium text-apolo-accentContrast transition hover:opacity-90 disabled:opacity-50"
              >
                {saving ? 'Salvando…' : 'Salvar'}
              </button>
              {saved && (
                <span className="inline-flex items-center gap-1 text-[11px] text-apolo-success">
                  <Check size={11} aria-hidden="true" /> Salvo
                </span>
              )}
              {error && <span className="text-[11px] text-apolo-error">{error}</span>}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

const INPUT_CLASS = 'w-full rounded-md border border-apolo-borderSubtle bg-apolo-bg px-2.5 py-1.5 text-xs text-apolo-textPrimary outline-none transition focus:border-apolo-accent font-mono';

function NtfyFieldset({ topic, setTopic, server, setServer, token, setToken, hasToken }) {
  return (
    <>
      <p className="text-[11px] leading-relaxed text-apolo-textTertiary">
        Instale o app <span className="font-medium text-apolo-textSecondary">ntfy</span> no
        celular e assine o tópico abaixo — o Apolo avisa quando um agente ou uma sessão
        termina. Tópico vazio = desligado.
      </p>
      <label className="flex flex-col gap-1 text-[11px] text-apolo-textSecondary">
        Tópico
        <div className="flex items-center gap-1.5">
          <input
            aria-label="Tópico ntfy"
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="apolo-a1b2c3…"
            className={INPUT_CLASS}
          />
          <button
            type="button"
            onClick={() => setTopic(randomTopic())}
            title="Gerar tópico aleatório (difícil de adivinhar)"
            className="shrink-0 rounded-md border border-apolo-borderSubtle p-1.5 text-apolo-textTertiary transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary"
          >
            <RefreshCw size={12} aria-hidden="true" />
          </button>
        </div>
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1 text-[11px] text-apolo-textSecondary">
          Servidor (opcional)
          <input
            aria-label="Servidor ntfy"
            type="text"
            value={server}
            onChange={(e) => setServer(e.target.value)}
            placeholder="https://ntfy.sh"
            className={INPUT_CLASS}
          />
        </label>
        <label className="flex flex-col gap-1 text-[11px] text-apolo-textSecondary">
          Access token (opcional{hasToken ? ' · configurado' : ''})
          <input
            aria-label="Token ntfy"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={hasToken ? '••••••••' : 'tk_…'}
            className={INPUT_CLASS}
          />
        </label>
      </div>
    </>
  );
}

function NtfyToggles({ notifyAgents, setNotifyAgents, notifySessions, setNotifySessions }) {
  return (
    <div className="flex items-center gap-4 text-[11px] text-apolo-textSecondary">
      <label className="flex cursor-pointer items-center gap-1.5">
        <input
          type="checkbox"
          checked={notifyAgents}
          onChange={(e) => setNotifyAgents(e.target.checked)}
          className="accent-[var(--apolo-accent)]"
        />
        Runs de agentes
      </label>
      <label className="flex cursor-pointer items-center gap-1.5">
        <input
          type="checkbox"
          checked={notifySessions}
          onChange={(e) => setNotifySessions(e.target.checked)}
          className="accent-[var(--apolo-accent)]"
        />
        Fim de turno de sessões
      </label>
    </div>
  );
}
